import type { Project, ProjectId } from "@storytell/types";

export type PendingWrite = { project: Project; createdAt: number };

// A create can return 200 before its grant is visible to projects-sync, so an
// optimistic write is kept across stale syncs. Bound that by wall-clock time,
// not sync count: refetch fires on every cp.grants event workspace-wide, so a
// count would be burned by unrelated grant churn and drop the write before its
// own grant propagates. Once the TTL passes, a create that never materialized
// server-side stops pinning a phantom project. See ENG-6516.
export const PENDING_WRITE_TTL_MS = 60_000;

// Overlay optimistic writes onto a fresh server sync: keep a pending project
// until the server owns it or it outlives the grace period, otherwise a stale
// sync (grant visibility lag) drops a just-created project and blanks its title.
// `changed` is true when any entry was pruned, so the caller only rewrites the
// pending map when it actually shrank. See ENG-6516.
export const mergePendingWrites = (
  serverProjects: Record<ProjectId, Project>,
  pending: Record<ProjectId, PendingWrite>,
  now: number
): { merged: Record<ProjectId, Project>; nextPending: Record<ProjectId, PendingWrite>; changed: boolean } => {
  const merged: Record<ProjectId, Project> = { ...serverProjects };
  const nextPending: Record<ProjectId, PendingWrite> = {};
  let changed = false;
  for (const [id, entry] of Object.entries(pending) as [ProjectId, PendingWrite][]) {
    if (serverProjects[id] || now - entry.createdAt >= PENDING_WRITE_TTL_MS) {
      changed = true;
      continue;
    }
    merged[id] = entry.project;
    nextPending[id] = entry;
  }
  return { merged, nextPending, changed };
};
